/**
 * Come funziona l'AI in questo portale — public transparency page.
 * ----------------------------------------------------------------
 * v1.44 — EU AI Act, Art. 4 (AI literacy) + Art. 50 (transparency).
 *
 * This page satisfies the "AI literacy" duty (Feb 2025 in force) by
 * giving any user — client, staff, curious visitor — a plain-Italian
 * breakdown of:
 *   1. which AI features exist in the portal
 *   2. which model / provider generates them
 *   3. what data leaves the portal to reach that provider
 *   4. what the provider does NOT do (training opt-out)
 *   5. how to spot an AI-generated output (the AiBadge convention)
 *
 * Kept as static content deliberately: it must be readable without
 * login (no auth guard) and indexable, so /come-funziona-lai is a
 * public route. Legal team can revise the copy without touching JS.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Info, ArrowLeft } from "lucide-react";
import { AiBadge } from "../components/AiBadge";
/* eslint-disable react/no-unescaped-entities */
// Italian prose contains many apostrophes ("l'AI", "com'è"); escaping
// each individually degrades readability of the source. The rendered
// output is identical.

const FEATURES = [
  {
    name: "Idee video del giorno",
    where: "Dashboard cliente → Cosa fare oggi",
    what: "Ogni notte generiamo 3-5 spunti creativi per Reel/Storie basati sul tuo posizionamento.",
    model: "Claude Sonnet (Anthropic) via chiave universale Emergent",
    data: "Nome salone, città, provincia, testo del posizionamento che hai scritto. Nessun dato di contatto (email, telefono).",
  },
  {
    name: "Rifinitura del posizionamento",
    where: "Impostazioni cliente → Il mio posizionamento",
    what: "Prendi il tuo testo grezzo e l'AI lo rifinisce mantenendo il tuo tono, aggiungendo città e provincia.",
    model: "Claude Sonnet (Anthropic) via chiave universale Emergent",
    data: "Il testo che scrivi tu + nome salone + città + provincia. Nome del referente NON inviato.",
  },
  {
    name: "Creatore di Offerte Irresistibili",
    where: "Dashboard cliente → Strumenti AI",
    what: "Componi un'offerta commerciale a partire da 3-4 tue scelte (obiettivo, target, mese). L'AI ci mette copy e struttura.",
    model: "Claude Haiku 4.5 (Anthropic) via chiave universale Emergent",
    data: "Le tue scelte dai menù + posizionamento + nome del salone. Nessun listino prezzi personale.",
  },
  {
    name: "Copertine generate",
    where: "Dashboard cliente → Copertine",
    what: "Immagini per Reel/Storie generate su tuoi prompt in 3 stili diversi.",
    model: "Gemini Nano Banana (Google) via chiave universale Emergent",
    data: "Il testo dell'immagine che vuoi + palette + eventuale riferimento visivo. Nessun dato del salone tranne il nome se lo includi tu.",
  },
];

export default function ComeFunzionaLai() {
  return (
    <div className="min-h-screen bg-white text-[var(--mp-ink)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
        <Link
          to="/"
          data-testid="ai-page-back-home"
          className="inline-flex items-center gap-2 text-sm text-[var(--mp-muted)] hover:text-[var(--mp-ink)] mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Torna alla home
        </Link>

        <div className="space-y-3 mb-10">
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
            Trasparenza AI · Regolamento UE 2024/1689
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight">
            Come usiamo l'intelligenza artificiale in questo portale
          </h1>
          <p className="text-base text-[var(--mp-muted)] max-w-2xl leading-relaxed">
            Alcune funzioni di questo portale usano modelli di AI generativa
            per aiutarti a creare contenuti più velocemente. Questa pagina
            ti spiega, in modo diretto, dove interviene l'AI, quali dati
            riceve e cosa NON fa mai.
          </p>
        </div>

        <section className="rounded-3xl border-2 border-[var(--mp-ink)] bg-[var(--mp-yellow)]/20 p-5 sm:p-6 mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5" />
            <div className="font-display text-xl">
              Come riconosci un contenuto generato con AI
            </div>
          </div>
          <p className="text-sm leading-relaxed">
            Ogni testo, immagine o proposta prodotta da un modello di
            intelligenza artificiale è marcata visibilmente con un badge:
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <AiBadge />
            <AiBadge variant="inline" />
            <span className="text-xs text-[var(--mp-muted)]">
              (formato grande / inline)
            </span>
          </div>
          <p className="text-sm mt-3 leading-relaxed">
            Se non vedi il badge, il contenuto è stato scritto o creato
            manualmente dallo staff dell'agenzia o dal cliente stesso.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="font-display text-2xl mb-4">
            Le funzioni AI attive sul portale
          </h2>
          <div className="space-y-4">
            {FEATURES.map((f) => (
              <article
                key={f.name}
                data-testid={`ai-feature-${f.name.toLowerCase().replace(/\s/g, "-")}`}
                className="rounded-2xl border border-[var(--mp-ink)] p-4 sm:p-5 bg-white"
              >
                <div className="font-display text-lg leading-tight">{f.name}</div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--mp-muted)] mt-1">
                  Dove lo trovi: <span className="text-[var(--mp-ink)] normal-case tracking-normal font-normal">{f.where}</span>
                </div>
                <p className="text-sm text-[var(--mp-ink)] mt-2 leading-relaxed">
                  {f.what}
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1">
                      Modello / provider
                    </div>
                    <div>{f.model}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1">
                      Cosa inviamo
                    </div>
                    <div>{f.data}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border-2 border-emerald-400 bg-emerald-50 p-5 sm:p-6 mb-8">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-emerald-700 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-xl mb-2">
                Cosa i provider AI NON fanno con i tuoi dati
              </h2>
              <ul className="space-y-2 text-sm leading-relaxed">
                <li>
                  <strong>Nessun addestramento sui tuoi contenuti.</strong>{" "}
                  I provider (Anthropic, Google) che usiamo tramite la
                  chiave universale Emergent hanno l'opzione "no training
                  on your data" attiva di default per la modalità API.
                </li>
                <li>
                  <strong>Nessuna condivisione con terzi.</strong>{" "}
                  I prompt sono elaborati per rispondere alla tua richiesta e
                  poi scartati dai provider entro pochi giorni (retention
                  breve, tipicamente ≤30gg).
                </li>
                <li>
                  <strong>Nessun profiling automatico.</strong>{" "}
                  L'AI non prende decisioni su di te (nessun credit scoring,
                  nessuna valutazione automatica): produce solo bozze di
                  contenuti che tu decidi se pubblicare o no.
                </li>
                <li>
                  <strong>Marcatura crittografica dell&rsquo;output.</strong>{" "}
                  I contenuti generati sono contrassegnati in modo
                  invisibile all'occhio ma verificabile da tool esterni
                  (C2PA / SynthID) — è il modo standard per certificare
                  l'origine AI.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-5 sm:p-6 mb-8 flex items-start gap-3">
          <Info className="h-5 w-5 text-[var(--mp-muted)] shrink-0 mt-1" />
          <div className="text-sm leading-relaxed">
            <strong>Rileggi sempre prima di pubblicare.</strong>{" "}
            L'AI è brava a produrre bozze veloci ma può commettere errori
            di fatto, tono o coerenza. Il contenuto finale è responsabilità
            tua: adattalo alla tua voce, verifica dati e prezzi, correggi
            le imprecisioni.
          </div>
        </section>

        <section className="text-xs text-[var(--mp-muted)] space-y-1 border-t border-[var(--mp-border)] pt-6">
          <div>
            Per il trattamento dei dati personali vedi la{" "}
            <Link to="/privacy-policy" className="underline">
              Privacy Policy
            </Link>{" "}
            e le{" "}
            <Link to="/note-legali" className="underline">
              Note legali
            </Link>
            .
          </div>
          <div>
            Riferimenti normativi: <em>Regolamento (UE) 2024/1689 (AI Act)</em>,
            Art. 4 (alfabetizzazione AI) e Art. 50 (obblighi di trasparenza).
          </div>
        </section>
      </div>
    </div>
  );
}
